import React, { useEffect, Suspense, lazy, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Contact from '../Contact';
import DigitalAccessibility from '../DigitalAccessibility';
import EaaCountdown from '../EaaCountdown';
import Hero from '../Hero';
import ComplianceAudit from '../ComplianceAudit';
import WhoWeServe from '../WhoWeServe';
import Footer from '../Footer';
import Navbar from '../Navbar';
import Pricing from '../../pages/Pricing';
import Report from '../../pages/Report/Report';
import Accessibility from '../../pages/Accessibility';
import ScrollToTop from '../ScrollToTop';
import '../../styles/ErrorBoundary.scss';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Error in component:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h2>Something went wrong.</h2>
          <button onClick={() => this.setState({ hasError: false })}>
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Homepage component that combines all sections
const HomePage = () => {
  const location = useLocation();
  const contactRef = React.useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    // Check if we should display contact section immediately
    const showContact = sessionStorage.getItem('showContact');
    if (showContact === 'true' && contactRef.current) {
      // Clear the flag
      sessionStorage.removeItem('showContact');
      // Scroll directly to the contact section without setting hash
      const yOffset = 0; // Adjust based on navbar height
      const y = contactRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y });
    } 
    // Or handle smooth scrolling if coming with state
    else if (location.state && location.state.scrollToContact && contactRef.current) {
      setTimeout(() => {
        contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500); // Delay to ensure DOM is fully loaded
    }
  }, [location.state]);

  return (
    <>
      <Navbar />
      <main id="main-content" className="overflow-x-hidden" aria-label="Main content">
        <ErrorBoundary>
          <Hero />
        </ErrorBoundary>
        <ErrorBoundary>
          <DigitalAccessibility />
        </ErrorBoundary>
        <ErrorBoundary>
          <EaaCountdown />
        </ErrorBoundary>
        <ErrorBoundary>
          <WhoWeServe />
        </ErrorBoundary>
        <ErrorBoundary>
          <ComplianceAudit />
        </ErrorBoundary>
        <ErrorBoundary>
          <div ref={contactRef} id="contact">
            <Contact />
          </div>
        </ErrorBoundary>
      </main>
      <Footer />
    </>
  );
};

const App: React.FC = () => {
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    // Initialize Google Analytics with error handling
    try {
      if (!analyticsInitialized) {
        ReactGA.initialize(process.env.REACT_APP_GA_4_ID as string);
        setAnalyticsInitialized(true);
      }
      
      // Track page views with error handling
      ReactGA.send({ hitType: 'pageview', page: location.pathname });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }, [location, analyticsInitialized]);

  return (
    <div className="min-h-screen">
      <ErrorBoundary>
        <ScrollToTop />
      </ErrorBoundary>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/report" element={<Report />} />
          <Route path="/accessibility/:slug" element={<Accessibility />} />
        </Routes>
      </ErrorBoundary>
    </div>
  );
};

export default App;
